import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import Icon from "@hackclub/icons";
import type { DraftTimelapse, Timelapse } from "@hackclub/lapse-api"
import { decryptData, fromHex } from "@hackclub/lapse-shared";
import clsx from "clsx";

import { deviceStorage } from "@/deviceStorage";
import { mediaFetch } from "@/safety";
import { useAdminView } from "@/hooks/useAdminView";
import { navigateTo } from "@/navigation";

import { ProfilePicture } from "@/components/entity/ProfilePicture"
import { Bullet } from "@/components/ui/Bullet";
import { TimeAgo } from "@/components/TimeAgo";
import { Duration } from "@/components/Duration";
import { AdminOnly, StatusBadge, timelapseStatus } from "@/components/ui/AdminOnly";

const thumbnailCache = new Map<string, string>();

export function TimelapseCard({ timelapse }: {
  timelapse: DraftTimelapse | Timelapse
}) {
  const router = useRouter();
  const adminView = useAdminView();
  const [thumb, setThumb] = useState<string | null>(null);
  const [missingKey, setMissingKey] = useState(false);

  useEffect(() => {
    if (timelapse.isDraft) {
      // If this is a draft, the thumbnail is encrypted. We deliberately don't retry aggressively here: a missing
      // object (an orphaned draft whose bytes were never uploaded) or a blocked CDN would otherwise turn every card
      // into a retry storm. `mediaFetch` tolerates a single transient blip, and anything past that degrades to a
      // placeholder.
      (async () => {
        if (thumbnailCache.has(timelapse.previewThumbnail)) {
          setThumb(thumbnailCache.get(timelapse.previewThumbnail)!);
          return;
        }

        const device = await deviceStorage.getDevice(timelapse.deviceId);
        if (!device) {
          setMissingKey(true);
          return;
        }

        try {
          const res = await mediaFetch(timelapse.previewThumbnail);
          if (!res.ok) {
            return;
          }

          const encryptedThumb = await res.arrayBuffer();

          const url = URL.createObjectURL(
            new Blob([
              await decryptData(
                fromHex(device.passkey).buffer,
                fromHex(timelapse.iv).buffer,
                encryptedThumb
              )
            ], { type: "image/webp" })
          );

          thumbnailCache.set(timelapse.previewThumbnail, url);
          setThumb(url);
        }
        catch {
        }
      })();

      return;
    }

    // The timelapse is public, and thus the thumbnail is NOT encrypted.
    setThumb(timelapse.thumbnailUrl);
  }, [timelapse]);

  const isAdminOnly = !timelapse.isDraft && adminView.isAdminOnly(timelapse);
  const href = `/${timelapse.isDraft ? "draft" : "timelapse"}/${timelapse.id}`;

  const card = (
    <article
      onClick={e => navigateTo(router, href, e)}
      onAuxClick={e => e.button === 1 && navigateTo(router, href, e)}
      role="button"
      className={clsx(
        "flex flex-col cursor-pointer sm:max-w-80",
        !timelapse.isDraft && "gap-4 sm:gap-5",
        timelapse.isDraft && "gap-3"
      )}
    >
      <div role="img" className="relative w-full aspect-video rounded-lg sm:rounded-2xl overflow-hidden">
        {
          !thumb
            ? (
              <div className="bg-slate w-full h-full flex items-center justify-center">
                {missingKey && <Icon glyph="private" size={32} className="text-muted" />}
              </div>
            )
            : <img src={thumb} alt="" className="block w-full h-full transition-all hover:brightness-75 object-cover" />
        }

        {!timelapse.isDraft && adminView.enabled && (
          <StatusBadge
            status={timelapseStatus(timelapse)}
            className="absolute top-1 left-1 sm:top-2 sm:left-2 bg-black/80!"
          />
        )}

        {!timelapse.isDraft && timelapse.duration > 0 && (
          <div className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 bg-black/80 text-white text-xs sm:text-sm px-1 sm:px-1.5 py-0.5 rounded font-medium">
            <Duration seconds={timelapse.duration} />
          </div>
        )}
      </div>
      
      <div className="flex gap-2 sm:gap-3 w-full justify-center items-center sm:items-start">
        { !timelapse.isDraft && <ProfilePicture user={timelapse.owner} size="sm" className="" /> }

        <div className="flex flex-col w-full">
           <h1 className="font-bold text-md leading-none sm:leading-normal sm:text-xl wrap-break-word">{timelapse.name ?? "(untitled)"}</h1>
          <h2 className={clsx(
            "text-md sm:text-xl text-secondary",
            !timelapse.isDraft && "flex gap-1 sm:gap-2"
          )}>
            {
              !timelapse.isDraft
                ? (
                  <>
                    <span className="truncate">@{timelapse.owner.displayName}</span>
                    <Bullet />
                    <TimeAgo date={timelapse.createdAt} className="shrink-0" />
                  </>
                ) : (
                  <>
                    Created <b><TimeAgo date={timelapse.createdAt} className="shrink-0" /></b>
                  </>
                )
            }
          </h2>
        </div>
      </div>
    </article>
  );

  if (isAdminOnly)
    return <AdminOnly className="p-2 sm:max-w-84">{card}</AdminOnly>;

  return card;
}
